import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { useProject } from '../contexts/ProjectContext';
import { ReactComponent as UploadArrowIcon } from "../assets/upload-arrow.svg";
import AssetManagement, { AssetInfoBase, ResetFileInput } from './AssetManagement';
import { isProjectOwnedAssetUrl } from '../utils/assetOwnership';
import { trackEvent } from '../utils/analytics';
import {
  detectVariableFontConfig,
  FontMetadata,
  VariableFontConfig,
} from '../utils/fontHelpers';

export const USER_UPLOADED_FONT_FAMILY = 'User Uploaded';

export type FontSource = 'default' | 'user';

export interface FontInfo extends AssetInfoBase {
  familyName: string;
  fileName?: string;
  source: FontSource;
  variableFontConfig?: VariableFontConfig;
}

export interface FontFamilyInfo {
  familyName: string;
  fonts: FontInfo[];
}

interface FontManagementProps {
  onFontListChange: (fontFamilies: FontFamilyInfo[]) => void;
  initialFonts?: FontFamilyInfo[];
  onFontUploaded?: (fontName: string, fontMetadata: FontMetadata) => void;
  onFontDeleted?: (fontName: string) => void;
}

const FontManagement: React.FC<FontManagementProps> = ({ onFontListChange, initialFonts = [], onFontUploaded, onFontDeleted }) => {
  const { selectedProject } = useProject();
  const selectedProjectId: string | undefined = selectedProject?.id;
  const [fontFamilies, setFontFamilies] = useState<FontFamilyInfo[]>(initialFonts);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLicenseDialogOpen, setIsLicenseDialogOpen] = useState<boolean>(false);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);

  const pendingResetRef = useRef<ResetFileInput | null>(null);
  const onFontListChangeRef = useRef(onFontListChange);
  const fontListChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const availableFontsRef = useRef<FontInfo[]>([]);
  const fontsLoadingRef = useRef<Promise<FontInfo[]> | null>(null);
  const fonts: FontInfo[] = useMemo(
    () => fontFamilies.flatMap((fontFamily: FontFamilyInfo) => fontFamily.fonts),
    [fontFamilies]
  );

  const groupFonts = useCallback((nextFonts: FontInfo[]): FontFamilyInfo[] => {
    const groupedFonts: Map<string, FontInfo[]> = new Map();

    for (const font of nextFonts) {
      const familyName: string = font.familyName || USER_UPLOADED_FONT_FAMILY;
      const familyFonts: FontInfo[] = groupedFonts.get(familyName) ?? [];
      familyFonts.push({ ...font, familyName });
      groupedFonts.set(familyName, familyFonts);
    }

    return Array.from(groupedFonts.entries())
      .map(([familyName, familyFonts]) => ({ familyName, fonts: familyFonts }));
  }, []);

  useEffect(() => {
    onFontListChangeRef.current = onFontListChange;
  }, [onFontListChange]);

  const scheduleFontListChange = useCallback((nextFamilies: FontFamilyInfo[]): void => {
    if (fontListChangeTimerRef.current) {
      clearTimeout(fontListChangeTimerRef.current);
    }
    fontListChangeTimerRef.current = setTimeout(() => {
      fontListChangeTimerRef.current = null;
      onFontListChangeRef.current(nextFamilies);
    }, 250);
  }, []);

  useEffect(() => {
    return () => {
      if (fontListChangeTimerRef.current) {
        clearTimeout(fontListChangeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    availableFontsRef.current = [];
    fontsLoadingRef.current = null;
  }, [selectedProjectId]);

  const getAvailableFonts = useCallback(async (): Promise<FontInfo[]> => {
    if (availableFontsRef.current.length > 0) {
      return availableFontsRef.current;
    }
    if (fontsLoadingRef.current) {
      return fontsLoadingRef.current;
    }

    fontsLoadingRef.current = (async () => {
      try {
        const response = await authFetch(`${getApiUrl()}/available-fonts?projectId=${selectedProject?.id}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        if (result.status === 'OK') {
          const availableFonts: FontInfo[] = result.files.map((font: { name: string; filename: string; url: string }) => ({
            name: font.name,
            familyName: USER_UPLOADED_FONT_FAMILY,
            fileName: font.filename,
            file: null,
            url: `${window.location.origin}${font.url}`,
            enabled: false,
            source: 'user' as const,
          }));
          availableFontsRef.current = availableFonts;
          return availableFonts;
        }
      } catch (err) {
        console.error('Failed to load available fonts:', err);
      }

      return [];
    })();

    return fontsLoadingRef.current;
  }, [selectedProject?.id]);

  useEffect(() => {
    setFontFamilies(initialFonts);
    getAvailableFonts().then((availableFonts: FontInfo[]) => {
      if (availableFonts.length === 0) {
        return;
      }

      setFontFamilies((previousFamilies: FontFamilyInfo[]) => {
        const previousFonts: FontInfo[] = previousFamilies.flatMap((fontFamily: FontFamilyInfo) => fontFamily.fonts);
        const existingNamesLower: Set<string> = new Set(previousFonts.map((font: FontInfo) => font.name.toLowerCase()));
        const newFonts: FontInfo[] = availableFonts.filter((font: FontInfo) => !existingNamesLower.has(font.name.toLowerCase()));
        if (newFonts.length === 0) {
          return previousFamilies;
        }
        return groupFonts([...previousFonts, ...newFonts]);
      });
    });
  }, [getAvailableFonts, groupFonts, initialFonts]);

  const uploadFontToAPI = async (
    file: File,
    variableFontConfig: VariableFontConfig | null
  ) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await authFetch(
        `${getApiUrl()}/upload-font?projectId=${selectedProject!.id}`,
        { method: "POST", body: formData }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();

      if (result.status === "OK") {
        setError(null);
        console.log("Font uploaded and loaded:", file.name);
        // Build server font URL as: <server-origin>/users/<projectId>/fonts/<filename>
        const projectId: string = selectedProject!.id;
        const filename: string = encodeURIComponent(file.name);
        const serverFontUrl = `${window.location.origin}/users/${projectId}/fonts/${filename}`;
        console.log("Server font URL:", serverFontUrl);
        if (onFontUploaded) {
          onFontUploaded(file.name.replace(/\.[^/.]+$/, ''), {
            url: serverFontUrl,
            variableFontConfig: variableFontConfig ?? undefined,
          });
        }
        trackEvent('font_uploaded', {
          project_id: selectedProject?.id,
          font_name: file.name.replace(/\.[^/.]+$/, ''),
        });
      } else {
        setError(result.message || "Unknown error occurred");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload font");
    } finally {
      setLoading(false);
    }
  };

  const processFontFiles = async (files: File[]): Promise<void> => {
    const existingNamesLower: Set<string> = new Set(fonts.map((f) => f.name.toLowerCase()));
    const addedNamesLower: Set<string> = new Set<string>();
    const newFonts: FontInfo[] = [];

    for (const file of files) {
      const fontName: string = file.name.replace(/\.[^/.]+$/, '');
      const nameLower: string = fontName.toLowerCase();

      if (existingNamesLower.has(nameLower) || addedNamesLower.has(nameLower)) {
        continue;
      }

      addedNamesLower.add(nameLower);
      const url: string = URL.createObjectURL(file);
      let variableFontConfig: VariableFontConfig | null = null;

      try {
        variableFontConfig = await detectVariableFontConfig(file, fontName);
      } catch (detectErr: unknown) {
        console.warn(`Failed to inspect font "${fontName}" for variable axes:`, detectErr);
      }

      try {
        const fontFace: FontFace = new FontFace(fontName, `url(${url})`);
        fontFace
          .load()
          .then((loadedFace: FontFace) => {
            const fontSet: FontFaceSet | undefined = (document as Document & { fonts?: FontFaceSet }).fonts;
            fontSet?.add(loadedFace);
          })
          .catch((loadErr: unknown) => {
            // Avoid an unhandled promise rejection ("A network error occurred.") while keeping uploads functional.
            console.warn(`Failed to load font "${fontName}" for preview:`, loadErr);
          })
          .finally(() => {
            // Free the blob URL once the browser attempted to load it.
            URL.revokeObjectURL(url);
          });
      } catch {}

      uploadFontToAPI(file, variableFontConfig);

      newFonts.push({
        name: fontName,
        familyName: USER_UPLOADED_FONT_FAMILY,
        fileName: file.name,
        file,
        url,
        enabled: true,
        source: 'user',
        variableFontConfig: variableFontConfig ?? undefined,
      });
    }

    if (newFonts.length > 0) {
      const updatedFonts: FontInfo[] = [...fonts, ...newFonts];
      const updatedFontFamilies: FontFamilyInfo[] = groupFonts(updatedFonts);
      setFontFamilies(updatedFontFamilies);
      onFontListChange(updatedFontFamilies);
    }
  };

  const handleFilesSelected = (files: File[], resetFileInput: ResetFileInput): void => {
    pendingResetRef.current = resetFileInput;
    setPendingFiles(files);
    setIsLicenseDialogOpen(true);
  };

  const handleLicenseCancel = (): void => {
    setIsLicenseDialogOpen(false);
    setPendingFiles(null);
    pendingResetRef.current?.();
    pendingResetRef.current = null;
  };

  const handleLicenseConfirm = (): void => {
    if (pendingFiles && pendingFiles.length > 0) {
      void processFontFiles(pendingFiles);
    }
    setIsLicenseDialogOpen(false);
    setPendingFiles(null);
    pendingResetRef.current?.();
    pendingResetRef.current = null;
  };

  const handleDeleteFont = async (font: FontInfo, idx: number): Promise<void> => {
    const fontName: string = font.name;
    const fontFileName: string = font.fileName || (font.url.split('/').pop() ?? '');

    if (!window.confirm(t`Are you sure you want to delete the font "${fontName}"?`)) {
      return;
    }

    try {
      const response = await authFetch(
        `${getApiUrl()}/delete-font?projectId=${selectedProject!.id}&fontName=${encodeURIComponent(fontFileName)}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove from local state
      const updatedFonts: FontInfo[] = fonts.filter((_, i) => i !== idx);
      const updatedFontFamilies: FontFamilyInfo[] = groupFonts(updatedFonts);
      setFontFamilies(updatedFontFamilies);
      onFontListChange(updatedFontFamilies);
      
      // Notify parent of deletion
      if (onFontDeleted) {
        onFontDeleted(fontName);
      }
      trackEvent('font_deleted', {
        project_id: selectedProject?.id,
        font_name: fontName,
      });
    } catch (err) {
      console.error('Error deleting font:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete font');
    }
  };

  return (
    <div>
      <Dialog open={isLicenseDialogOpen} onClose={handleLicenseCancel} fullWidth maxWidth="sm">
        <DialogTitle>{t`Font License Confirmation`}</DialogTitle>
        <DialogContent>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {t`By uploading a font to the system, I confirm that I hold a valid and legal license to use this font, and that I am not infringing any copyright or terms of use. I acknowledge that full responsibility for the use of the font lies solely with me. This font will be available to me in all my projects.`}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLicenseCancel}>{t`Cancel`}</Button>
          <Button onClick={handleLicenseConfirm} variant="contained">{t`Confirm`}</Button>
        </DialogActions>
      </Dialog>

      <AssetManagement<FontInfo>
        title={String(t`Font Management`)}
        uploadTitle={String(t`Upload Fonts`)}
        uploadTooltip={String(t`Supported formats: .ttf, .otf, .woff, .woff2`)}
        uploadAccept=".ttf,.otf,.woff,.woff2"
        uploadAriaLabel={String(t`Upload fonts`)}
        uploadIcon={<UploadArrowIcon width={24} height={24} style={{ display: 'block' }} aria-hidden="true" focusable="false" />}
        onFilesSelected={handleFilesSelected}
        listLabel={String(t`Font List`)}
        listTooltip={String(t`Checked fonts will be available in the Block Properties section.`)}
        searchPlaceholder={String(t`Search fonts...`)}
        emptyText={String(t`No fonts uploaded yet.`)}
        noMatchText={String(t`No fonts match your search.`)}
        loading={loading}
        loadingText={String(t`Uploading font...`)}
        error={error}
        assets={fonts}
        onAssetsChange={(next) => {
          const updatedFontFamilies: FontFamilyInfo[] = groupFonts(next);
          setFontFamilies(updatedFontFamilies);
          scheduleFontListChange(updatedFontFamilies);
        }}
        getGroupLabel={(font) => font.familyName}
        isDeletable={(font) => !!font.file || isProjectOwnedAssetUrl(font.url, selectedProjectId, 'fonts')}
        deleteTitle={String(t`Delete font`)}
        onDeleteAsset={(font, idx) => {
          void handleDeleteFont(font, idx);
        }}
      />
    </div>
  );
};

export default FontManagement;
